import type { components } from "@scalty/contracts";
import { useQueryClient } from "@tanstack/vue-query";
import {
  computed,
  effectScope,
  reactive,
  ref,
  shallowRef,
  watch,
  type ComputedRef,
  type EffectScope,
  type Ref,
} from "vue";

import {
  DATA_OBJECT_COUNTS_ACTIVE_INTERVAL_MS,
  DATA_OBJECT_COUNTS_IDLE_INTERVAL_MS,
  MAX_UPLOAD_CONTROL_REQUESTS,
  MAX_UPLOAD_PIPELINES,
  UploadTransferError,
  createBoundedUploadQueue,
  dataObjectCountsPollIntervalMs,
  dataObjectCreateRequest,
  retryUploadOperation,
  uploadFailurePhase,
  uploadFailureReceipt,
  type UploadReceiptPhase,
} from "../utils/dataObjectUploadPolicy";

type DataObjectUploadTicket = components["schemas"]["DataObjectUploadTicket"];
type DataObjectRevision = components["schemas"]["DataObjectRevision"];
type DataObjectCompletion = components["schemas"]["DataObjectCompletion"];
type DataObjectList = components["schemas"]["DataObjectList"];
export type DataObjectUploadCounts = components["schemas"]["DataObjectStateCounts"];

export type UploadPhase = UploadReceiptPhase;

export interface UploadSelection {
  file: File;
  /** Folder-relative display path when the browser provides one. */
  displayName?: string;
}

export interface UploadRefusal {
  displayName: string;
  reason: string;
}

export interface UploadItem {
  key: string;
  fileName: string;
  sizeBytes: number | null;
  /** Stable across retries, so create replay cannot duplicate the object. */
  idempotencyKey: string;
  /** One Add data dialog session; stable across every retry of this item. */
  uploadBatchId: string;
  phase: UploadPhase;
  /** The browser's own view of the transfer — never a stored product value. */
  progress: number;
  dataObjectId: string | null;
  revision: DataObjectRevision | null;
  problem: string | null;
  requestId: string | null;
  /** True when the browser stopped retrying but the customer can resume it. */
  retryable: boolean;
}

interface UploadStoreState {
  items: UploadItem[];
  batchCounts: DataObjectUploadCounts | null;
  countsFailure: string | null;
}

interface UploadRuntime {
  key: string;
  projectId: string;
  organizationId: string | null;
  state: UploadStoreState;
  files: Map<string, File>;
  retainedFileCount: Ref<number>;
  itemIndex: Map<string, number>;
  lifetime: AbortController;
  countsTimer: ReturnType<typeof setTimeout> | null;
  countsRunning: boolean;
  countsBatchId: string | null;
  previousCounts: DataObjectUploadCounts | null;
  settlementInvalidated: boolean;
  queryClient: ReturnType<typeof useQueryClient>;
  signOutScope: EffectScope | null;
}

// Control and byte caps cover every project in this browser, including batches
// that continue after their originating route unmounts.
const createQueue = createBoundedUploadQueue(MAX_UPLOAD_CONTROL_REQUESTS);
const transferQueue = createBoundedUploadQueue(MAX_UPLOAD_PIPELINES);
const completeQueue = createBoundedUploadQueue(MAX_UPLOAD_CONTROL_REQUESTS);
const uploadStores = new Map<string, UploadRuntime>();

function emptyStoreState(): UploadStoreState {
  return { items: [], batchCounts: null, countsFailure: null };
}

function uploadStoreKey(organizationId: string | null, projectId: string): string {
  return `${organizationId ?? "session"}:${projectId}`;
}

function refuseBeforeCreate(file: File): string | null {
  if (file.size === 0) return "This file is empty. Add a file that contains data.";
  if (file.size > MAX_SOURCE_BYTES) {
    return "One upload can carry up to 5 GiB — split this file into smaller ones.";
  }
  return null;
}

export function sourceContentType(file: File): string {
  if (file.type.trim() !== "") return file.type;
  if (file.name.toLowerCase().endsWith(".md")) return MARKDOWN_CONTENT_TYPE;
  return "application/octet-stream";
}

export function putSource(
  url: string,
  file: File,
  contentType: string,
  onProgress: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    let settled = false;
    function finish(action: () => void): void {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", onAbort);
      action();
    }
    function onAbort(): void {
      request.abort();
      finish(() => reject(signal?.reason ?? new DOMException("Upload cancelled", "AbortError")));
    }

    request.open("PUT", url);
    request.setRequestHeader("Content-Type", contentType);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    request.onload = () => finish(() => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new UploadTransferError("The file transfer was refused.", request.status));
    });
    request.onerror = () => finish(() =>
      reject(new UploadTransferError("The connection dropped during the transfer.", null)));
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    request.send(file);
  });
}

function patch(runtime: UploadRuntime, key: string, change: Partial<UploadItem>): void {
  const index = runtime.itemIndex.get(key);
  if (index === undefined) return;
  runtime.state.items[index] = { ...runtime.state.items[index], ...change } as UploadItem;
}

function retainFile(runtime: UploadRuntime, key: string, file: File): void {
  if (!runtime.files.has(key)) runtime.retainedFileCount.value += 1;
  runtime.files.set(key, file);
}

function releaseFile(runtime: UploadRuntime, key: string): void {
  if (runtime.files.delete(key)) runtime.retainedFileCount.value -= 1;
}

function releaseAllFiles(runtime: UploadRuntime): void {
  runtime.files.clear();
  runtime.retainedFileCount.value = 0;
}

function waitForRetry(signal: AbortSignal, milliseconds: number): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, milliseconds);
    function onAbort(): void {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
      reject(signal.reason);
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function applyFailure(runtime: UploadRuntime, key: string, error: unknown): void {
  const failure = uploadFailureReceipt(error);
  const phase = uploadFailurePhase(error);
  if (phase === "refused") releaseFile(runtime, key);
  patch(runtime, key, {
    phase,
    problem: failure.reason,
    requestId: failure.requestId,
    retryable: failure.retryable,
  });
}

function clearCountsTimer(runtime: UploadRuntime): void {
  if (runtime.countsTimer === null) return;
  clearTimeout(runtime.countsTimer);
  runtime.countsTimer = null;
}

function localTransferActive(runtime: UploadRuntime): boolean {
  return runtime.state.items.some((item) =>
    item.phase === "queued"
    || item.phase === "creating"
    || item.phase === "uploading"
    || item.phase === "finalizing");
}

function serverRowsKnown(runtime: UploadRuntime): number {
  return runtime.state.items.filter((item) => item.dataObjectId !== null).length;
}

function countsAreSettled(runtime: UploadRuntime, counts: DataObjectUploadCounts): boolean {
  return !localTransferActive(runtime)
    && counts.checking === 0
    && counts.total >= serverRowsKnown(runtime);
}

function scheduleCountsPoll(runtime: UploadRuntime, delayMs: number): void {
  if (runtime.lifetime.signal.aborted || runtime.countsRunning || runtime.countsTimer !== null) {
    return;
  }
  runtime.countsTimer = setTimeout(() => {
    runtime.countsTimer = null;
    void pollCounts(runtime);
  }, delayMs);
}

async function pollCounts(runtime: UploadRuntime): Promise<void> {
  const batchId = runtime.countsBatchId;
  if (runtime.lifetime.signal.aborted || runtime.countsRunning || batchId === null) return;
  runtime.countsRunning = true;
  let nextDelay: number | null = DATA_OBJECT_COUNTS_IDLE_INTERVAL_MS;
  try {
    const query = new URLSearchParams({
      project: runtime.projectId,
      batch: batchId,
      limit: "1",
    });
    const response = await apiFetch<DataObjectList>(`/data-objects?${query.toString()}`, {
      signal: runtime.lifetime.signal,
    });
    if (response.counts === undefined) {
      runtime.state.countsFailure =
        "Upload totals are temporarily unavailable; transfer work continues.";
    } else {
      const current = response.counts;
      runtime.state.countsFailure = null;
      nextDelay = dataObjectCountsPollIntervalMs(runtime.previousCounts, current);
      runtime.previousCounts = current;
      runtime.state.batchCounts = current;
      if (countsAreSettled(runtime, current)) {
        nextDelay = null;
        if (!runtime.settlementInvalidated) {
          runtime.settlementInvalidated = true;
          void runtime.queryClient.invalidateQueries({ queryKey: ["dataObjects"] });
        }
      }
    }
  } catch {
    if (runtime.lifetime.signal.aborted) nextDelay = null;
    else {
      runtime.state.countsFailure =
        "Upload totals are temporarily unavailable; transfer work continues.";
    }
  } finally {
    runtime.countsRunning = false;
    if (nextDelay !== null) scheduleCountsPoll(runtime, nextDelay);
  }
}

function ensureCountsPoll(runtime: UploadRuntime, batchId: string): void {
  if (runtime.countsBatchId !== batchId) {
    clearCountsTimer(runtime);
    runtime.countsBatchId = batchId;
    runtime.previousCounts = null;
    runtime.settlementInvalidated = false;
    runtime.state.batchCounts = null;
    runtime.state.countsFailure = null;
  }
  scheduleCountsPoll(runtime, DATA_OBJECT_COUNTS_ACTIVE_INTERVAL_MS);
}

function cancelRuntime(runtime: UploadRuntime, reason: string): void {
  if (!runtime.lifetime.signal.aborted) {
    runtime.lifetime.abort(new DOMException(reason, "AbortError"));
  }
  clearCountsTimer(runtime);
  for (const item of runtime.state.items) {
    if (item.phase === "refused" || item.phase === "cancelled") continue;
    patch(runtime, item.key, {
      phase: "cancelled",
      problem: reason,
      requestId: null,
      retryable: false,
    });
  }
  releaseAllFiles(runtime);
}

function observeAuthenticationScope(
  runtime: UploadRuntime,
  session: ReturnType<typeof useSession>,
): void {
  if (runtime.organizationId === null || runtime.signOutScope !== null) return;
  const scope = effectScope(true);
  runtime.signOutScope = scope;
  scope.run(() => {
    watch(() => session.value.data?.session.activeOrganizationId ?? null, (organizationId) => {
      if (organizationId === runtime.organizationId) return;
      cancelRuntime(runtime, "Uploads stopped because the signed-in organization changed.");
      scope.stop();
      if (runtime.signOutScope === scope) runtime.signOutScope = null;
    });
  });
}

function runtimeBusy(runtime: UploadRuntime): boolean {
  if (localTransferActive(runtime)) return true;
  const counts = runtime.state.batchCounts;
  return runtime.state.items.some((item) => item.phase === "checking")
    && (counts === null || !countsAreSettled(runtime, counts));
}

async function run(runtime: UploadRuntime, key: string, signal: AbortSignal): Promise<void> {
  const file = runtime.files.get(key);
  const index = runtime.itemIndex.get(key);
  const item = index === undefined ? undefined : runtime.state.items[index];
  if (!file || !item) return;
  const retryOptions = {
    attempts: Number.POSITIVE_INFINITY,
    sleep: (milliseconds: number) => waitForRetry(signal, milliseconds),
  };

  try {
    const ticket = await createQueue.enqueue(async () => {
      if (signal.aborted) throw signal.reason;
      patch(runtime, key, {
        phase: "creating",
        problem: null,
        requestId: null,
        progress: 0,
        retryable: false,
      });
      return retryUploadOperation(
        () => apiFetch<DataObjectUploadTicket>(
          "/data-objects",
          {
            ...dataObjectCreateRequest({
              projectId: runtime.projectId,
              fileName: item.fileName,
              contentType: sourceContentType(file),
              sizeBytes: file.size,
              idempotencyKey: item.idempotencyKey,
              uploadBatchId: item.uploadBatchId,
            }),
            signal,
          },
        ),
        retryOptions,
      );
    });
    if (signal.aborted) throw signal.reason;

    patch(runtime, key, {
      phase: "queued",
      dataObjectId: ticket.dataObject.id,
      revision: ticket.dataObjectRevision,
    });
    await transferQueue.enqueue(async () => {
      if (signal.aborted) throw signal.reason;
      patch(runtime, key, { phase: "uploading" });
      await retryUploadOperation(
        async (attempt) => {
          if (attempt > 1) patch(runtime, key, { progress: 0 });
          await putSource(
            ticket.upload.url,
            file,
            ticket.upload.contentType,
            (percent) => patch(runtime, key, { progress: percent }),
            signal,
          );
        },
        retryOptions,
      );
    });
    if (signal.aborted) throw signal.reason;

    patch(runtime, key, { phase: "checking", progress: 100 });
    const completion = await completeQueue.enqueue(async () => {
      if (signal.aborted) throw signal.reason;
      patch(runtime, key, { phase: "finalizing" });
      return retryUploadOperation(
        () => apiFetch<DataObjectCompletion>(
          `/data-objects/${encodeURIComponent(ticket.dataObject.id)}/revisions/${encodeURIComponent(ticket.dataObjectRevision.id)}/complete`,
          { method: "POST", signal },
        ),
        retryOptions,
      );
    });
    if (signal.aborted) throw signal.reason;

    patch(runtime, key, {
      phase: "checking",
      revision: completion.dataObjectRevision,
    });
    releaseFile(runtime, key);
    ensureCountsPoll(runtime, item.uploadBatchId);
  } catch (error) {
    if (!signal.aborted) applyFailure(runtime, key, error);
  }
}

/**
 * One organization/project-keyed upload store owns a batch for the browser app
 * lifetime. Route and dialog unmounts only detach views; explicit cancellation
 * or leaving the authenticated organization aborts work and releases bytes.
 */
export function useDataObjectUploads(
  projectId: Ref<string | null> | ComputedRef<string | null>,
) {
  const queryClient = useQueryClient();
  const session = useSession();
  const activeOrganizationId = computed(
    () => session.value.data?.session.activeOrganizationId ?? null,
  );
  const emptyState = reactive(emptyStoreState());
  const selectedRuntime = shallowRef<UploadRuntime | null>(null);

  function selectExisting(): void {
    const selectedProject = projectId.value;
    if (selectedProject === null) {
      selectedRuntime.value = null;
      return;
    }
    selectedRuntime.value = uploadStores.get(
      uploadStoreKey(activeOrganizationId.value, selectedProject),
    ) ?? null;
  }
  watch([projectId, activeOrganizationId], selectExisting, { immediate: true, flush: "sync" });

  function storeFor(selectedProject: string): UploadRuntime {
    const organizationId = activeOrganizationId.value;
    const key = uploadStoreKey(organizationId, selectedProject);
    let runtime = uploadStores.get(key);
    if (runtime === undefined) {
      runtime = {
        key,
        projectId: selectedProject,
        organizationId,
        state: reactive(emptyStoreState()),
        files: new Map(),
        retainedFileCount: ref(0),
        itemIndex: new Map(),
        lifetime: new AbortController(),
        countsTimer: null,
        countsRunning: false,
        countsBatchId: null,
        previousCounts: null,
        settlementInvalidated: false,
        queryClient,
        signOutScope: null,
      };
      uploadStores.set(key, runtime);
    } else {
      runtime.queryClient = queryClient;
    }
    observeAuthenticationScope(runtime, session);
    if (projectId.value === selectedProject) selectedRuntime.value = runtime;
    return runtime;
  }

  function add(
    selected: readonly (File | UploadSelection)[],
    selectedProject: string,
    uploadBatchId: string,
  ): void {
    const runtime = storeFor(selectedProject);
    if (runtime.lifetime.signal.aborted) runtime.lifetime = new AbortController();
    const additions: UploadItem[] = [];
    const queued: string[] = [];
    for (const selection of selected) {
      const file = selection instanceof File ? selection : selection.file;
      const displayName = selection instanceof File
        ? (selection.webkitRelativePath || selection.name)
        : (selection.displayName || selection.file.webkitRelativePath || selection.file.name);
      const key = crypto.randomUUID();
      const refusal = refuseBeforeCreate(file);
      if (refusal === null) retainFile(runtime, key, file);
      runtime.itemIndex.set(key, runtime.state.items.length + additions.length);
      additions.push({
        key,
        fileName: displayName,
        sizeBytes: file.size,
        idempotencyKey: crypto.randomUUID(),
        uploadBatchId,
        phase: refusal === null ? "queued" : "refused",
        progress: 0,
        dataObjectId: null,
        revision: null,
        problem: refusal,
        requestId: null,
        retryable: false,
      });
      if (refusal === null) queued.push(key);
    }
    runtime.state.items.push(...additions);
    const signal = runtime.lifetime.signal;
    for (const key of queued) void run(runtime, key, signal);
  }

  function refuse(refusals: readonly UploadRefusal[], uploadBatchId: string): void {
    const selectedProject = projectId.value;
    if (selectedProject === null) return;
    const runtime = storeFor(selectedProject);
    for (const refusal of refusals) {
      const key = crypto.randomUUID();
      runtime.itemIndex.set(key, runtime.state.items.length);
      runtime.state.items.push({
        key,
        fileName: refusal.displayName,
        sizeBytes: null,
        idempotencyKey: crypto.randomUUID(),
        uploadBatchId,
        phase: "refused",
        progress: 0,
        dataObjectId: null,
        revision: null,
        problem: refusal.reason,
        requestId: null,
        retryable: false,
      });
    }
  }

  function retry(key: string, selectedProject: string): void {
    const runtime = storeFor(selectedProject);
    const index = runtime.itemIndex.get(key);
    const item = index === undefined ? undefined : runtime.state.items[index];
    if (!item || item.phase !== "retry_waiting" || !item.retryable) return;
    if (runtime.lifetime.signal.aborted) runtime.lifetime = new AbortController();
    patch(runtime, key, {
      phase: "queued",
      problem: null,
      requestId: null,
      retryable: false,
    });
    void run(runtime, key, runtime.lifetime.signal);
  }

  function cancel(): void {
    const runtime = selectedRuntime.value;
    if (runtime === null) return;
    cancelRuntime(runtime, "Cancelled in this browser. Files already received may keep checking.");
  }

  function clear(): void {
    const runtime = selectedRuntime.value;
    if (runtime === null || runtimeBusy(runtime)) return;
    clearCountsTimer(runtime);
    releaseAllFiles(runtime);
    runtime.itemIndex.clear();
    runtime.state.items = [];
    runtime.state.batchCounts = null;
    runtime.state.countsFailure = null;
    runtime.signOutScope?.stop();
    runtime.signOutScope = null;
    if (uploadStores.get(runtime.key) === runtime) uploadStores.delete(runtime.key);
    selectedRuntime.value = null;
  }

  function refreshList(): void {
    void queryClient.invalidateQueries({ queryKey: ["dataObjects"] });
  }

  const items = computed(() => selectedRuntime.value?.state.items ?? emptyState.items);
  const batchCounts = computed(
    () => selectedRuntime.value?.state.batchCounts ?? emptyState.batchCounts,
  );
  const countsFailure = computed(
    () => selectedRuntime.value?.state.countsFailure ?? emptyState.countsFailure,
  );
  const busy = computed(() => {
    const runtime = selectedRuntime.value;
    return runtime === null ? false : runtimeBusy(runtime);
  });
  const retainedFileCount = computed(
    () => selectedRuntime.value?.retainedFileCount.value ?? 0,
  );

  return {
    items,
    batchCounts,
    countsFailure,
    add,
    refuse,
    retry,
    cancel,
    clear,
    busy,
    retainedFileCount,
    refreshList,
  };
}
